import type { ReactNode } from "react";
import { applications } from "../config/applications";
import { ApplicationCard } from "./ApplicationCard";
import { Icon } from "./Icon";

interface SectionCardProps {
  id: "applications" | "services";
  title: string;
  description: string;
  countLabel: string;
  icon: "apps" | "spark";
  children: ReactNode;
}

export function SectionCard({ id, title, description, countLabel, icon, children }: SectionCardProps) {
  return (
    <details className="section-card" id={id} open>
      <summary className="section-card__summary">
        <span className="section-card__identity">
          <span className="section-card__icon"><Icon name={icon} /></span>
          <span>
            <span className="section-card__title" role="heading" aria-level={2}>{title}</span>
            <span className="section-card__description">{description}</span>
          </span>
        </span>
        <span className="section-card__meta">
          <span className="section-card__count">{countLabel}</span>
          <span className="section-card__toggle">
            <span className="section-card__expanded-label">Minimize</span>
            <span className="section-card__collapsed-label">Expand</span>
            <Icon name="chevron" />
          </span>
        </span>
      </summary>
      <div className="section-card__content">
        <div className="section-card__grid">{children}</div>
      </div>
    </details>
  );
}

export function ApplicationsSection() {
  const applicationItems = applications.filter((application) => application.section === "applications");
  const serviceItems = applications.filter((application) => application.section === "services");

  return (
    <section className="catalogue-section" aria-label="Application and service catalogue">
      <div className="container catalogue-stack">
        <SectionCard
          id="applications"
          title="Applications"
          description="Access available PRT Technology applications and view their current service status."
          countLabel={`${applicationItems.length} applications`}
          icon="apps"
        >
          {applicationItems.map((application) => <ApplicationCard key={application.id} application={application} />)}
        </SectionCard>

        <SectionCard
          id="services"
          title="Services"
          description="Departmental request services for drawings, projects, and scope changes."
          countLabel={`${serviceItems.length} services`}
          icon="spark"
        >
          {serviceItems.map((application) => <ApplicationCard key={application.id} application={application} />)}
        </SectionCard>
      </div>
    </section>
  );
}

export function InformationSection() {
  return (
    <div className="container information-wrap">
      <section className="information-panel" id="about" aria-labelledby="about-title">
        <span className="information-panel__icon"><Icon name="info" /></span>
        <div>
          <h2 id="about-title">About PRT Technology</h2>
          <p>PRT Technology develops and evaluates digital tools, automation solutions, data applications, and AI-enabled systems for manufacturing operations.</p>
          <ul aria-label="Technology focus areas">
            <li>Digital manufacturing</li><li>Automation</li><li>Data analytics</li><li>Artificial intelligence</li>
          </ul>
        </div>
      </section>

      <section className="information-panel" aria-labelledby="disclaimer-title">
        <span className="information-panel__icon"><Icon name="shield" /></span>
        <div>
          <h2 id="disclaimer-title">Demonstration notice</h2>
          <p>This portal is intended for demonstration and controlled testing. Applications shown here may not represent production-approved systems.</p>
          <p>Applications may change, become temporarily unavailable, or contain incomplete features during evaluation.</p>
        </div>
      </section>
    </div>
  );
}
